import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["password", "passwordConfirmation", "submit", "lengthIcon", "specialIcon"]

  connect() {
    this.validateForm()
  }

  passwordTargetConnected() {
    this.passwordTarget.addEventListener("input", () => this.validatePassword())
  }

  passwordConfirmationTargetConnected() {
    this.passwordConfirmationTarget.addEventListener("input", () => this.validatePasswordConfirmation())
  }

  validatePassword() {
    const password = this.passwordTarget.value
    const minLength = 8
    const hasMinLength = password.length >= minLength
    const hasSpecialChar = /[^a-zA-Z0-9]/.test(password)

    this.updateRequirementIcon("lengthIcon", hasMinLength, password.length > 0)
    this.updateRequirementIcon("specialIcon", hasSpecialChar, password.length > 0)

    if (password.length > 0 && (!hasMinLength || !hasSpecialChar)) {
      const messages = []
      if (!hasMinLength) messages.push(`at least ${minLength} characters`)
      if (!hasSpecialChar) messages.push("a special character")
      this.setFieldError(this.passwordTarget, `Password needs ${messages.join(" and ")}`)
    } else {
      this.clearFieldError(this.passwordTarget)
    }

    this.validatePasswordConfirmation()
    this.validateForm()
  }

  updateRequirementIcon(targetName, met, started) {
    if (!this[`has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`]) return

    const icon = this[`${targetName}Target`]
    if (!started) {
      icon.textContent = "\u25CB"
      icon.className = "requirement-icon text-gray-400"
    } else if (met) {
      icon.textContent = "\u2713"
      icon.className = "requirement-icon text-green-500"
    } else {
      icon.textContent = "\u2717"
      icon.className = "requirement-icon text-red-400"
    }
  }

  validatePasswordConfirmation() {
    if (!this.hasPasswordConfirmationTarget) return

    const password = this.passwordTarget.value
    const confirmation = this.passwordConfirmationTarget.value

    if (confirmation && password !== confirmation) {
      this.setFieldError(this.passwordConfirmationTarget, "Passwords don't match")
    } else {
      this.clearFieldError(this.passwordConfirmationTarget)
    }

    this.validateForm()
  }

  validateForm() {
    const hasErrors = this.element.querySelectorAll(".border-red-500").length > 0
    const password = this.passwordTarget.value
    const isEmpty = password.length === 0
    const hasMinLength = password.length >= 8
    const hasSpecialChar = /[^a-zA-Z0-9]/.test(password)
    const isValid = hasMinLength && hasSpecialChar

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = hasErrors || isEmpty || !isValid

      if (hasErrors || isEmpty || !isValid) {
        this.submitTarget.classList.add("opacity-50", "cursor-not-allowed")
        this.submitTarget.classList.remove("hover:bg-blue-700")
      } else {
        this.submitTarget.classList.remove("opacity-50", "cursor-not-allowed")
        this.submitTarget.classList.add("hover:bg-blue-700")
      }
    }
  }

  setFieldError(field, message) {
    field.classList.add("border-red-500", "focus:border-red-500", "focus:ring-red-500")
    field.classList.remove("border-gray-300", "focus:border-blue-500", "focus:ring-blue-500")

    const existingError = field.parentNode.querySelector(".text-red-600")
    if (existingError) {
      existingError.remove()
    }

    const errorDiv = document.createElement("p")
    errorDiv.className = "mt-1 text-sm text-red-600"
    errorDiv.textContent = message
    field.parentNode.appendChild(errorDiv)
  }

  clearFieldError(field) {
    field.classList.remove("border-red-500", "focus:border-red-500", "focus:ring-red-500")
    field.classList.add("border-gray-300", "focus:border-blue-500", "focus:ring-blue-500")

    const existingError = field.parentNode.querySelector(".text-red-600")
    if (existingError) {
      existingError.remove()
    }
  }
}
